import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  FiBriefcase,
  FiUserCheck,
  FiUserMinus,
  FiUsers,
  FiUserX,
} from "react-icons/fi";

import API from "../../../../../../utils/apiCalling";

import {
  config,
} from "../../../../../../utils/apiUrl";

import EmployeePageHeader
  from "../common/EmployeePageHeader";

import EmployeeModuleTabs
  from "../common/EmployeeModuleTabs";

import EmployeeFilters
  from "./EmployeeFilters";

import EmployeeTable
  from "./EmployeeTable";

import AddEditEmployee
  from "./AddEditEmployee";

import EmployeeDetailsDrawer
  from "./EmployeeDetailsDrawer";

import ChangeEmployeeStatus
  from "./ChangeEmployeeStatus";

import {
  INITIAL_EMPLOYEE_FILTERS,
} from "../utils/employee.constants";

import {
  getEmployeeApiError,
} from "../utils/employee.utils";

import "./EmployeeManagement.css";


const INITIAL_PAGINATION = {
  page: 1,
  limit: 10,
  totalRecords: 0,
  totalPages: 1,
};


const INITIAL_SUMMARY = {
  total: 0,
  active: 0,
  inactive: 0,
  resigned: 0,
  terminated: 0,
};


const INITIAL_DROPDOWN_DATA = {
  departments: [],
  designations: [],
  statuses: [],
  employmentTypes: [],
  roles: [],
  permissions: [],
  permissionModules: [],
};


/*
|--------------------------------------------------------------------------
| EMPLOYEE MANAGEMENT
|--------------------------------------------------------------------------
*/

const EmployeeManagement = () => {

  const api =
    useMemo(
      () =>
        new API(),
      []
    );


  /*
  |--------------------------------------------------------------------------
  | DATA
  |--------------------------------------------------------------------------
  */

  const [
    employees,
    setEmployees,
  ] =
    useState([]);


  const [
    dropdownData,
    setDropdownData,
  ] =
    useState({
      ...INITIAL_DROPDOWN_DATA,
    });


  const [
    summary,
    setSummary,
  ] =
    useState({
      ...INITIAL_SUMMARY,
    });


  /*
  |--------------------------------------------------------------------------
  | FILTER
  |--------------------------------------------------------------------------
  */

  const [
    filters,
    setFilters,
  ] =
    useState({
      ...INITIAL_EMPLOYEE_FILTERS,
    });


  const [
    appliedFilters,
    setAppliedFilters,
  ] =
    useState({
      ...INITIAL_EMPLOYEE_FILTERS,
    });


  /*
  |--------------------------------------------------------------------------
  | PAGINATION
  |--------------------------------------------------------------------------
  */

  const [
    pagination,
    setPagination,
  ] =
    useState({
      ...INITIAL_PAGINATION,
    });


  /*
  |--------------------------------------------------------------------------
  | LOADING / ERROR
  |--------------------------------------------------------------------------
  */

  const [
    loading,
    setLoading,
  ] =
    useState(false);


  const [
    dropdownLoading,
    setDropdownLoading,
  ] =
    useState(false);


  const [
    error,
    setError,
  ] =
    useState("");


  /*
  |--------------------------------------------------------------------------
  | SELECTED
  |--------------------------------------------------------------------------
  */

  const [
    selectedEmployee,
    setSelectedEmployee,
  ] =
    useState(null);


  /*
  |--------------------------------------------------------------------------
  | MODALS
  |--------------------------------------------------------------------------
  */

  const [
    addEditOpen,
    setAddEditOpen,
  ] =
    useState(false);


  const [
    detailsOpen,
    setDetailsOpen,
  ] =
    useState(false);


  const [
    statusDialogOpen,
    setStatusDialogOpen,
  ] =
    useState(false);


  /*
  |--------------------------------------------------------------------------
  | LOAD DROPDOWNS
  |--------------------------------------------------------------------------
  */

  const loadDropdownData =
    useCallback(
      async () => {

        try {

          setDropdownLoading(
            true
          );


          const response =
            await api.get(
              config.employeeDropdownData
            );


          if (
            response?.status !==
            "success"
          ) {

            throw new Error(
              response?.message ||
              "Unable to load employee configuration."
            );

          }


          const data =
            response?.data ||
            {};


          setDropdownData({

            departments:
              Array.isArray(
                data.departments
              )
                ?
                data.departments
                :
                [],

            designations:
              Array.isArray(
                data.designations
              )
                ?
                data.designations
                :
                [],

            statuses:
              Array.isArray(
                data.statuses
              )
                ?
                data.statuses
                :
                [],

            employmentTypes:
              Array.isArray(
                data.employmentTypes
              )
                ?
                data.employmentTypes
                :
                [],

            roles:
              Array.isArray(
                data.roles
              )
                ?
                data.roles
                :
                [],

            permissions:
              Array.isArray(
                data.permissions
              )
                ?
                data.permissions
                :
                [],

            permissionModules:
              Array.isArray(
                data.permissionModules
              )
                ?
                data.permissionModules
                :
                [],

          });

        } catch (
          requestError
        ) {

          console.error(
            "Employee dropdown error:",
            requestError
          );


          setDropdownData({
            ...INITIAL_DROPDOWN_DATA,
          });

        } finally {

          setDropdownLoading(
            false
          );

        }

      },
      [
        api,
      ]
    );


  /*
  |--------------------------------------------------------------------------
  | LOAD EMPLOYEES
  |--------------------------------------------------------------------------
  */

  const loadEmployees =
    useCallback(
      async () => {

        try {

          setLoading(
            true
          );


          setError(
            ""
          );


          const response =
            await api.post(
              config.getEmployees,
              {
                page:
                  pagination.page,

                limit:
                  pagination.limit,

                search:
                  appliedFilters.search,

                status:
                  appliedFilters.status,

                department:
                  appliedFilters.department,

                employmentType:
                  appliedFilters.employmentType,

                roleId:
                  appliedFilters.roleId,
              }
            );


          if (
            response?.status !==
            "success"
          ) {

            throw new Error(
              response?.message ||
              "Unable to load employees."
            );

          }


          const records =
            Array.isArray(
              response?.data
            )
              ?
              response.data
              :
              [];


          const totalRecords =
            Number(
              response
                ?.pagination
                ?.totalRecords ??
              records.length
            );


          const totalPages =
            Math.max(
              Number(
                response
                  ?.pagination
                  ?.totalPages
              ) ||
              Math.ceil(
                totalRecords /
                pagination.limit
              ) ||
              1,
              1
            );


          setEmployees(
            records
          );


          setPagination(
            (
              previous
            ) => ({
              ...previous,

              totalRecords,

              totalPages,
            })
          );


          /*
          |--------------------------------------------------------------------------
          | SUMMARY
          |--------------------------------------------------------------------------
          */

          if (
            response?.summary
          ) {

            setSummary({
              ...INITIAL_SUMMARY,
              ...response.summary,
            });

          } else {

            const calculated = {
              ...INITIAL_SUMMARY,

              total:
                totalRecords,
            };


            records.forEach(
              (
                employee
              ) => {

                const status =
                  String(
                    employee?.status ||
                    ""
                  )
                    .trim()
                    .toLowerCase();


                if (
                  Object.prototype
                    .hasOwnProperty
                    .call(
                      calculated,
                      status
                    )
                ) {

                  calculated[
                    status
                  ] +=
                    1;

                }

              }
            );


            setSummary(
              calculated
            );

          }

        } catch (
          requestError
        ) {

          console.error(
            "Load employees error:",
            requestError
          );


          setEmployees(
            []
          );


          setError(
            getEmployeeApiError(
              requestError,
              "Unable to load employees."
            )
          );

        } finally {

          setLoading(
            false
          );

        }

      },
      [
        api,
        appliedFilters,
        pagination.page,
        pagination.limit,
      ]
    );


  /*
  |--------------------------------------------------------------------------
  | INITIAL LOAD
  |--------------------------------------------------------------------------
  */

  useEffect(
    () => {

      loadDropdownData();

    },
    [
      loadDropdownData,
    ]
  );


  useEffect(
    () => {

      loadEmployees();

    },
    [
      loadEmployees,
    ]
  );


  /*
  |--------------------------------------------------------------------------
  | SEARCH
  |--------------------------------------------------------------------------
  */

  const handleSearch =
    () => {

      setPagination(
        (
          previous
        ) => ({
          ...previous,
          page: 1,
        })
      );


      setAppliedFilters({
        ...filters,

        search:
          String(
            filters.search ||
            ""
          ).trim(),
      });

    };


  /*
  |--------------------------------------------------------------------------
  | RESET
  |--------------------------------------------------------------------------
  */

  const handleReset =
    () => {

      setFilters({
        ...INITIAL_EMPLOYEE_FILTERS,
      });


      setAppliedFilters({
        ...INITIAL_EMPLOYEE_FILTERS,
      });


      setPagination(
        (
          previous
        ) => ({
          ...previous,
          page: 1,
        })
      );

    };


  /*
  |--------------------------------------------------------------------------
  | ADD
  |--------------------------------------------------------------------------
  */

  const handleAdd =
    () => {

      setSelectedEmployee(
        null
      );


      setAddEditOpen(
        true
      );

    };


  /*
  |--------------------------------------------------------------------------
  | EDIT
  |--------------------------------------------------------------------------
  */

  const handleEdit = (
    employee
  ) => {

    setSelectedEmployee(
      employee
    );


    setDetailsOpen(
      false
    );


    setAddEditOpen(
      true
    );

  };


  /*
  |--------------------------------------------------------------------------
  | VIEW
  |--------------------------------------------------------------------------
  */

  const handleView = (
    employee
  ) => {

    setSelectedEmployee(
      employee
    );


    setDetailsOpen(
      true
    );

  };


  /*
  |--------------------------------------------------------------------------
  | STATUS
  |--------------------------------------------------------------------------
  */

  const handleChangeStatus = (
    employee
  ) => {

    setSelectedEmployee(
      employee
    );


    setDetailsOpen(
      false
    );


    setStatusDialogOpen(
      true
    );

  };


  /*
  |--------------------------------------------------------------------------
  | SUCCESS
  |--------------------------------------------------------------------------
  */

  const handleOperationSuccess =
    async () => {

      setSelectedEmployee(
        null
      );


      setAddEditOpen(
        false
      );


      setDetailsOpen(
        false
      );


      setStatusDialogOpen(
        false
      );


      await Promise.all([
        loadEmployees(),
        loadDropdownData(),
      ]);

    };


  /*
  |--------------------------------------------------------------------------
  | SUMMARY CARDS
  |--------------------------------------------------------------------------
  */

  const summaryCards = [

    {
      title:
        "Total Employees",

      value:
        summary.total,

      icon:
        FiUsers,

      className:
        "employee-summary-blue",
    },

    {
      title:
        "Active",

      value:
        summary.active,

      icon:
        FiUserCheck,

      className:
        "employee-summary-green",
    },

    {
      title:
        "Inactive",

      value:
        summary.inactive,

      icon:
        FiUserMinus,

      className:
        "employee-summary-orange",
    },

    {
      title:
        "Resigned",

      value:
        summary.resigned,

      icon:
        FiBriefcase,

      className:
        "employee-summary-purple",
    },

    {
      title:
        "Terminated",

      value:
        summary.terminated,

      icon:
        FiUserX,

      className:
        "employee-summary-red",
    },

  ];


  return (

    <div className="employee-management-page">

      <EmployeePageHeader
        title="Employees"
        description="Manage employee records, login access, roles, permissions and salary configuration."
        onRefresh={
          () =>
            Promise.all([
              loadEmployees(),
              loadDropdownData(),
            ])
        }
        loading={
          loading ||
          dropdownLoading
        }
        hideAction
      />


      <EmployeeModuleTabs />


      <div className="employee-summary-grid">

        {
          summaryCards.map(
            (
              card
            ) => {

              const Icon =
                card.icon;


              return (

                <article
                  key={card.title}
                  className={`employee-summary-card ${card.className}`}
                >

                  <div className="employee-summary-icon">

                    <Icon />

                  </div>


                  <div>

                    <span>
                      {card.title}
                    </span>

                    <strong>
                      {
                        loading
                          ?
                          "..."
                          :
                          Number(
                            card.value ||
                            0
                          )
                      }
                    </strong>

                  </div>

                </article>

              );

            }
          )
        }

      </div>


      <EmployeeFilters
        filters={filters}
        setFilters={setFilters}
        dropdownData={dropdownData}
        loading={loading}
        dropdownLoading={
          dropdownLoading
        }
        onSearch={
          handleSearch
        }
        onReset={
          handleReset
        }
        onAddEmployee={
          handleAdd
        }
      />


      {
        error
          ?
          (
            <div className="employee-management-error">

              <strong>
                Unable to load employees
              </strong>

              <span>
                {error}
              </span>

            </div>
          )
          :
          null
      }


      <EmployeeTable
        employees={
          employees
        }
        loading={
          loading
        }
        page={
          pagination.page
        }
        limit={
          pagination.limit
        }
        totalRecords={
          pagination.totalRecords
        }
        totalPages={
          pagination.totalPages
        }
        onPageChange={
          (
            page
          ) =>
            setPagination(
              (
                previous
              ) => ({
                ...previous,
                page,
              })
            )
        }
        onLimitChange={
          (
            limit
          ) =>
            setPagination(
              (
                previous
              ) => ({
                ...previous,
                page: 1,
                limit,
              })
            )
        }
        onView={
          handleView
        }
        onEdit={
          handleEdit
        }
        onChangeStatus={
          handleChangeStatus
        }
      />


      <AddEditEmployee
        open={
          addEditOpen
        }
        employee={
          selectedEmployee
        }
        dropdownData={
          dropdownData
        }
        onClose={
          () => {

            setAddEditOpen(
              false
            );

            setSelectedEmployee(
              null
            );

          }
        }
        onSuccess={
          handleOperationSuccess
        }
      />


      <EmployeeDetailsDrawer
        open={
          detailsOpen
        }
        employee={
          selectedEmployee
        }
        onClose={
          () => {

            setDetailsOpen(
              false
            );

            setSelectedEmployee(
              null
            );

          }
        }
        onEdit={
          handleEdit
        }
        onChangeStatus={
          handleChangeStatus
        }
      />


      <ChangeEmployeeStatus
        open={
          statusDialogOpen
        }
        employee={
          selectedEmployee
        }
        onClose={
          () => {

            setStatusDialogOpen(
              false
            );

            setSelectedEmployee(
              null
            );

          }
        }
        onSuccess={
          handleOperationSuccess
        }
      />

    </div>

  );

};


export default EmployeeManagement;